// ─────────────────────────────────────────────────────────────────────────
// AV Simulation Tool — Client-side GLB Conversion Pipeline
//
// Loads OBJ / FBX / GLTF / GLB into a *hidden* Babylon scene, exports as GLB
// using Babylon's official GLTF2Export, captures a thumbnail from the same
// hidden scene, and returns blobs ready to upload via filesApi.upload().
//
// Why client-side: zero backend deps (no Blender, no FBX SDK, no obj2gltf),
// runs in seconds, reuses the same loaders the editor already has.
// ─────────────────────────────────────────────────────────────────────────
import * as BABYLON from '@babylonjs/core';
import '@babylonjs/loaders/OBJ';
import '@babylonjs/loaders/glTF';
import { GLTF2Export } from '@babylonjs/serializers/glTF';

export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024; // 200 MB hard cap
export const CONVERTIBLE_EXTS = ['obj', 'fbx', 'gltf', 'glb'];

export function getExt(name) {
  const m = (name || '').toLowerCase().match(/\.([a-z0-9]{2,5})(?:\?|$)/);
  return m ? m[1] : '';
}

/**
 * Convert an OBJ/FBX/GLTF/GLB File into an optimized GLB Blob + thumbnail.
 *
 * @param {File} file - User-selected file
 * @param {object} opts - { onProgress: (pct, label) => void }
 * @returns {Promise<{ glbBlob: Blob, thumbnailBlob: Blob|null, originalFormat: string, baseName: string }>}
 */
export async function convertFileToGLB(file, opts = {}) {
  const onProgress = opts.onProgress || (() => {});
  const ext = getExt(file.name);
  if (!CONVERTIBLE_EXTS.includes(ext)) {
    throw new Error(`Unsupported format: .${ext}. Allowed: ${CONVERTIBLE_EXTS.join(', ')}`);
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 200 MB)`);
  }

  // Ensure the FBX plugin is registered. The editor's BabylonScene registers
  // it globally on mount via BABYLON.SceneLoader.RegisterPlugin, so by the
  // time the Library panel runs we can rely on that — no extra wiring needed.
  // (No-op kept here for clarity if FBX support is ever isolated from editor.)

  // Hidden offscreen canvas + Babylon engine for parsing/exporting/thumbnailing.
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  canvas.style.position = 'absolute';
  canvas.style.left = '-9999px';
  canvas.style.top = '-9999px';
  document.body.appendChild(canvas);

  const engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
  const scene = new BABYLON.Scene(engine);
  scene.clearColor = new BABYLON.Color4(0.05, 0.06, 0.10, 1);

  // Lighting + camera so the thumbnail looks decent.
  const hemi = new BABYLON.HemisphericLight('h', new BABYLON.Vector3(0, 1, 0), scene);
  hemi.intensity = 0.8;
  const dir = new BABYLON.DirectionalLight('d', new BABYLON.Vector3(-0.5, -1, -0.5), scene);
  dir.intensity = 0.6;
  const camera = new BABYLON.ArcRotateCamera('cam', Math.PI / 4, Math.PI / 3, 5,
    BABYLON.Vector3.Zero(), scene);
  camera.minZ = 0.01;

  let glbBlob = null;
  let thumbnailBlob = null;
  const baseName = (file.name || 'model').replace(/\.[^.]+$/, '');

  try {
    // 1. Load the source file into the hidden scene.
    onProgress(10, 'Loading model…');
    const url = URL.createObjectURL(file);
    try {
      // ImportMeshAsync handles all four formats via the registered loaders.
      // We pass the file extension explicitly so Babylon picks the right plugin.
      await BABYLON.SceneLoader.AppendAsync('', url, scene, null, '.' + ext);
    } finally {
      URL.revokeObjectURL(url);
    }

    // 2. Frame the camera around all loaded meshes for a clean thumbnail.
    onProgress(40, 'Framing model…');
    const meshes = scene.meshes.filter(m => m.getTotalVertices && m.getTotalVertices() > 0);
    if (meshes.length === 0) {
      throw new Error('No geometry found in the file.');
    }
    let min = new BABYLON.Vector3(Infinity, Infinity, Infinity);
    let max = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);
    meshes.forEach(m => {
      const bi = m.getBoundingInfo();
      const wmin = bi.boundingBox.minimumWorld;
      const wmax = bi.boundingBox.maximumWorld;
      min = BABYLON.Vector3.Minimize(min, wmin);
      max = BABYLON.Vector3.Maximize(max, wmax);
    });
    const center = BABYLON.Vector3.Center(min, max);
    const size = max.subtract(min);
    const radius = Math.max(size.x, size.y, size.z, 1) * 1.2;
    camera.setTarget(center);
    camera.radius = radius * 2.5;

    // 3. Render once so the thumbnail captures the framed model.
    onProgress(55, 'Capturing thumbnail…');
    scene.render();
    await new Promise((res) => requestAnimationFrame(res));
    scene.render();
    thumbnailBlob = await new Promise((res) => {
      canvas.toBlob((b) => res(b), 'image/jpeg', 0.7);
    });

    // 4. Export the entire scene as GLB. Skip cameras + lights so we don't
    //    pollute downstream scenes with our thumbnail rig.
    onProgress(70, 'Converting to GLB…');
    const exported = await GLTF2Export.GLBAsync(scene, baseName, {
      shouldExportNode: (node) => {
        // Exclude our thumbnail-only camera & lights; keep imported geometry.
        if (node === camera || node === hemi || node === dir) return false;
        return true;
      },
    });

    // exported.glTFFiles is { 'name.glb': Blob } — grab the GLB.
    onProgress(90, 'Packaging…');
    const fileNames = Object.keys(exported.glTFFiles || {});
    const glbName = fileNames.find(n => n.toLowerCase().endsWith('.glb')) || fileNames[0];
    if (!glbName) throw new Error('GLB export produced no output');
    const glbData = exported.glTFFiles[glbName];
    glbBlob = glbData instanceof Blob ? glbData : new Blob([glbData], { type: 'model/gltf-binary' });

    onProgress(100, 'Done.');
  } finally {
    // Always clean up offscreen resources.
    try { scene.dispose(); } catch (e) { /* ignore */ }
    try { engine.dispose(); } catch (e) { /* ignore */ }
    try { canvas.remove(); } catch (e) { /* ignore */ }
  }

  if (!glbBlob) throw new Error('Conversion failed — no GLB produced');

  return {
    glbBlob,
    thumbnailBlob,
    originalFormat: ext,
    baseName,
  };
}
